import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { APP_CONFIG } from "../../constants/constants";
import { localStorageKeys } from "../../utils/localStorageItems";
import {
  CameraDeviceConfig,
  CameraEventGetVideoDetailResponse,
  CameraTaskProcessResponse,
  DeviceStorageListingPayload,
} from "../types/Camera";
import {
  AvilableDeviceModelResponse,
  CameraDetail,
  DataUsagePayload,
  DataUsageResponse,
  Device,
  DeviceGeneralInfo,
  DeviceGroup,
  DeviceListingPayload,
  DeviceReportSimple,
  DeviceReportSummary,
  DeviceStorageListResponse,
  DeviceTimelinePayload,
  DeviceTimelineResponse,
  EventComment,
  EventDetailsResponse,
  EventVideoDownloadResponse,
  FormatSDCardPayload,
  ICCHistory,
  LastParkingPhotoResponse,
  SDCardFileDeleteResponse,
  UpdateConfigurationSingleDevicePayload,
  VMListingResponse,
  VMResponse,
} from "../types/CamerasAdminVM";
import {
  VMDashNotifyResponse,
  VMLiveviewUrlResponse,
} from "../types/CamerasVisionMax";
import { preparedAuthHeader } from "../utils/utils";
import { API_METHODS } from "./constants";
import { VM_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

// Fetch request to download Event video file
export const downloadEventVideo = (
  downloadURL: string,
  name: string,
  onSuccess?: () => void,
  onError?: () => void
) => {
  fetch(downloadURL)
    .then((resp) => {
      return resp.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // the filename you want
      a.download = name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      onSuccess && onSuccess();
    })
    .catch(() => {
      onError && onError();
    });
};

export enum CamerasAdminVMApiTags {
  FORMAT_SD_CARD = "FormatSDCard",
  CAMERA_DETAILS = "CameraDetails",
  EVENT_COMMENTS = "EventComments",
  SD_CARD_FILE_DELETE = "SDCardFileDelete",
  CAMERA_DEVICE_LISTING = "CameraDeviceListing", // is this being used?
  SINGLE_CAMERA_CONFIG = "SingleCameraConfig",
}

// initial / raw base query (`camerasAdminVMAPIURLBase` is received after login)
const rawBaseQuery = fetchBaseQuery({
  baseUrl:
    localStorage.getItem(localStorageKeys.camerasAdminVMAPIURLBase) || "", // VM_API_SERVER_URL,
  prepareHeaders: async (headers) => {
    headers.set(
      "X-Api-Key",
      localStorage.getItem(localStorageKeys.camerasAdminVMAPIKey) || ""
      // `${
      //   VM_ENV === "prod"
      //     ? process.env.REACT_APP_VISIONMAX_KEY
      //     : process.env.REACT_APP_VISIONMAX_KEY_STAGING
      // }`
    );
    headers.set(
      "Authorization",
      preparedAuthHeader(
        localStorage.getItem(localStorageKeys.camerasAdminVMToken)
      )
    );
    return headers;
  },
});

// helps prepare `baseUrl` before making requests, and
// gracefully handle scenarios when the required data is unavailable
// source: https://github.com/reduxjs/redux-toolkit/issues/1657#issuecomment-952529533
// source: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#constructing-a-dynamic-base-url-using-redux-state
const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const urlBase =
    localStorage.getItem(localStorageKeys.camerasAdminVMAPIURLBase) || "";
  const apiKey =
    localStorage.getItem(localStorageKeys.camerasAdminVMAPIKey) || "";
  const accessToken =
    localStorage.getItem(localStorageKeys.camerasAdminVMToken) || "";
  // gracefully handle scenarios where data to generate the URL is missing
  if (!urlBase || !apiKey || !accessToken) {
    const unavailableParams = [];
    if (!urlBase) unavailableParams.push("urlBase");
    if (!apiKey) unavailableParams.push("apiKey");
    if (!accessToken) unavailableParams.push("accessToken");
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        data: `The credentials required to make camera related requests are not available${
          APP_CONFIG.DEBUG.ALL ? ` (${unavailableParams.join(", ")})` : ""
        }`,
      },
    };
  }

  const urlEnd = typeof args === "string" ? args : args.url;
  // construct a dynamically generated portion of the url - esp. prepend the urlBase
  const adjustedUrl = `${urlBase}${urlEnd}`;
  const adjustedArgs =
    typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
  // provide the amended url and other params to the raw base query
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

export const VMAdminAPIs = createApi({
  reducerPath: "vmApi",
  tagTypes: [
    CamerasAdminVMApiTags.FORMAT_SD_CARD,
    CamerasAdminVMApiTags.CAMERA_DETAILS,
    CamerasAdminVMApiTags.EVENT_COMMENTS,
    CamerasAdminVMApiTags.SD_CARD_FILE_DELETE,
    CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING,
    CamerasAdminVMApiTags.SINGLE_CAMERA_CONFIG,
  ],
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    // get all camera ids
    getAllDeviceIds: builder.query<
      VMListingResponse<Device>,
      DeviceListingPayload
    >({
      query: (params: DeviceListingPayload) => {
        return {
          url: `${VM_ENDPOINTS.GET_ALL_DEVICE_IDS}`,
          method: API_METHODS.GET,
          params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMListingResponse<Device>) => {
        return response;
      },
    }),

    // get all available models
    getAllAvailableDeviceModels: builder.query<
      AvilableDeviceModelResponse,
      void
    >({
      query: () => {
        return {
          url: `${VM_ENDPOINTS.GET_ALL_AVAILABLE_MODELS}`,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: AvilableDeviceModelResponse) => {
        return response;
      },
    }),

    // get all available models
    getLiveviewUrl: builder.query<VMLiveviewUrlResponse, string>({
      query: (serialNumber: string) => {
        return {
          url: `${VM_ENDPOINTS.GET_LIVEVIEW_URL}`,
          method: API_METHODS.GET,
          params: {
            serial_number: serialNumber,
          },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMLiveviewUrlResponse) => {
        return response;
      },
    }),

    getCameraDetails: builder.query<VMListingResponse<CameraDetail>, string>({
      query: (cameraId: string) => {
        return {
          url: `${VM_ENDPOINTS.DEVICE_DETAILS}/${cameraId}/`,
          method: API_METHODS.GET,
        };
      },
      providesTags: [CamerasAdminVMApiTags.CAMERA_DETAILS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMListingResponse<CameraDetail>) => {
        return response;
      },
    }),

    // find serial number by cids
    getCameraByCid: builder.query<VMListingResponse<CameraDetail>, string>({
      query: (cid: string) => {
        return {
          url: `${VM_ENDPOINTS.DEVICE_DETAILS}/${cid}/`,
          method: API_METHODS.GET,
        };
      },
      providesTags: [CamerasAdminVMApiTags.CAMERA_DETAILS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMListingResponse<CameraDetail>) => {
        return response;
      },
    }),

    // data usage
    getDataUsage: builder.query<DataUsageResponse, DataUsagePayload>({
      query: (params: DataUsagePayload) => {
        return {
          url: `${VM_ENDPOINTS.GET_DATA_USAGE}`,
          method: API_METHODS.GET,
          params: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: DataUsageResponse) => {
        return response;
      },
    }),

    // Group List
    getGroupList: builder.query<VMListingResponse<DeviceGroup>, void>({
      query: () => {
        return {
          url: `${VM_ENDPOINTS.GROUPS}`,
          method: API_METHODS.GET,
          params: { limit: 999 },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMListingResponse<DeviceGroup>) => {
        return response;
      },
    }),

    // Device Health
    getDeviceHealth: builder.query<VMResponse<DeviceReportSummary>, string>({
      query: (cameraId: string) => {
        return {
          url: `${VM_ENDPOINTS.DEVICE_HEALTH}/${cameraId}/`,
          method: API_METHODS.GET,
        };
      },
      providesTags: [CamerasAdminVMApiTags.FORMAT_SD_CARD],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<DeviceReportSummary>) => {
        return response;
      },
    }),

    // Simple Health report
    getDeviceHealthSimple: builder.query<
      VMResponse<DeviceReportSimple>,
      string
    >({
      query: (cameraId: string) => {
        return {
          url: `${VM_ENDPOINTS.DEVICE_HEALTH_SIMPLE}/${cameraId}/`,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<DeviceReportSimple>) => {
        return response;
      },
    }),

    // Format SD Card
    formatSDCard: builder.mutation<VMResponse<unknown>, FormatSDCardPayload>({
      query: (params: FormatSDCardPayload) => {
        return {
          url: `${VM_ENDPOINTS.FORMAT_SD_CARD}/${params.cameraId}/`,
          method: API_METHODS.POST,
          body: {
            type: params.type,
          },
        };
      },
      invalidatesTags: [CamerasAdminVMApiTags.FORMAT_SD_CARD],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<unknown>) => {
        return response;
      },
    }),

    // Device Health
    getDeviceICCHistory: builder.query<VMListingResponse<ICCHistory>, string>({
      query: (deviceId: string) => {
        return {
          url: `${VM_ENDPOINTS.ICC_HISTORY}`,
          method: API_METHODS.GET,
          params: {
            device_id: deviceId,
          },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMListingResponse<ICCHistory>) => {
        return response;
      },
    }),

    // Device Wakeup
    getDeviceWakeup: builder.query<VMResponse<any>, string>({
      query: (cameraId: string) => {
        return {
          url: `${VM_ENDPOINTS.DEVICE_WAKEUP}/${cameraId}`,
          method: API_METHODS.GET,
          params: {
            status: Math.random(),
          },
        };
      },
      onQueryStarted: async (cameraId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            VMAdminAPIs.util.invalidateTags([
              CamerasAdminVMApiTags.CAMERA_DETAILS,
            ])
          );
        } catch (error) {
          console.error("Error in getDiagnoseDevice:", error);
        }
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<any>) => {
        return response;
      },
    }),

    // Device General Info
    getDeviceGeneralInfo: builder.query<
      VMListingResponse<DeviceGeneralInfo>,
      string
    >({
      query: (cameraId: string) => {
        return {
          url: VM_ENDPOINTS.GENERAL_INFO,
          method: API_METHODS.GET,
          params: {
            cid: cameraId,
            status: Math.random(), // Add random param to always fetch latest data
          },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMListingResponse<DeviceGeneralInfo>) => {
        return response;
      },
    }),

    //update the configuration of single device
    updateConfigurationSingleDevice: builder.mutation<
      VMResponse<unknown>,
      UpdateConfigurationSingleDevicePayload
    >({
      query: ({
        cameraId,
        ...restParams
      }: UpdateConfigurationSingleDevicePayload) => {
        return {
          url: `${VM_ENDPOINTS.UPDATE_CONFIGURATION_SINGLE_DEVICE}/${cameraId}/`,
          method: API_METHODS.PATCH,
          body: restParams,
        };
      },
      invalidatesTags: [CamerasAdminVMApiTags.SINGLE_CAMERA_CONFIG],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<unknown>) => {
        return response;
      },
    }),
    getDiagnoseDevice: builder.query<VMResponse<any>, string>({
      query: (cameraId: string) => {
        return {
          url: `${VM_ENDPOINTS.DIAGNOSE_DEVICE}/${cameraId}`,
        };
      },
      onQueryStarted: async (cameraId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            VMAdminAPIs.util.invalidateTags([
              CamerasAdminVMApiTags.CAMERA_DETAILS,
            ])
          );
        } catch (error) {
          console.error("Error in getDiagnoseDevice:", error);
        }
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<any>) => {
        return response;
      },
    }),

    // get parking photos
    getLastParkingPhoto: builder.query<
      VMResponse<LastParkingPhotoResponse>,
      string
    >({
      query: (cameraId: string) => {
        return {
          url: `${VM_ENDPOINTS.LAST_PARKING_PHOTO}/${cameraId}`,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<LastParkingPhotoResponse>) => {
        return response;
      },
    }),
    //Get videos from SD card
    getDeviceStorageListing: builder.query<
      VMListingResponse<DeviceStorageListResponse>,
      DeviceStorageListingPayload
    >({
      query: (params: DeviceStorageListingPayload) => {
        return {
          url: VM_ENDPOINTS.DEVICE_STORAGE_LIST(params?.cid ?? ""),
          params: params,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: VMListingResponse<DeviceStorageListResponse>
      ) => {
        return response;
      },
    }),

    // get event details
    getEventDetails: builder.query<VMResponse<EventDetailsResponse[]>, string>({
      query: (ticketId: string) => {
        return {
          url: VM_ENDPOINTS.EVENT_DETAIL(ticketId),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<EventDetailsResponse[]>) => {
        return response;
      },
    }),

    // event get video details
    eventGetVideoDetails: builder.mutation<
      VMResponse<CameraEventGetVideoDetailResponse>,
      { ticketId: string }
    >({
      query: ({ ticketId }: { ticketId: string }) => {
        const formData = new FormData();
        formData.append("ticket", ticketId);
        // Note: The API expects a POST request with the ticket in the body
        // even though it might look like a GET request.
        // This is a workaround to match the API's expected format.
        return {
          url: `${VM_ENDPOINTS.EVENT_GET_VIDEO_DETAIL}`,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: VMResponse<CameraEventGetVideoDetailResponse>
      ) => {
        return response;
      },
    }),

    //Get Devices Timeline
    getDeviceTimeline: builder.query<
      VMResponse<DeviceTimelineResponse>,
      DeviceTimelinePayload
    >({
      query: (params: DeviceTimelinePayload) => {
        const formData = new FormData();

        // Append each field from `params` to the FormData object
        Object.entries(params).forEach(([key, value]) => {
          formData.append(key, value as string); // Ensure `value` is cast to string or handle other types
        });
        return {
          url: VM_ENDPOINTS.GET_TIMELINE,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<DeviceTimelineResponse>) => {
        return response;
      },
    }),

    // get event video download url
    getEventVideoDownloadUrl: builder.mutation<
      EventVideoDownloadResponse,
      { ticketId: string; fileName: string }
    >({
      query: ({
        ticketId,
        fileName,
      }: {
        ticketId: string;
        fileName: string;
      }) => {
        const formData = new FormData();
        formData.append("ticket", ticketId);
        formData.append("filename", fileName);
        // Note: The API expects a POST request with the ticket in the body
        // even though it might look like a GET request.
        // This is a workaround to match the API's expected format.
        return {
          url: `${VM_ENDPOINTS.DOWNLOAD_EVENT_VIDEO}`,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: EventVideoDownloadResponse) => {
        return response;
      },
    }),
    deleteSDCardFile: builder.mutation<
      SDCardFileDeleteResponse,
      { ticketId: string }
    >({
      query: ({ ticketId }: { ticketId: string; fileName: string }) => {
        const formData = new FormData();
        formData.append("ticket", ticketId);
        return {
          url: `${VM_ENDPOINTS.DELETE_SD_CARD_FILE}`,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [CamerasAdminVMApiTags.SD_CARD_FILE_DELETE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SDCardFileDeleteResponse) => {
        return response;
      },
    }),

    // get event comments
    getEventComments: builder.query<VMResponse<EventComment[]>, string>({
      query: (ticketId: string) => {
        return {
          url: VM_ENDPOINTS.GET_EVENT_COMMENTS(ticketId),
          method: API_METHODS.GET,
        };
      },
      providesTags: [CamerasAdminVMApiTags.EVENT_COMMENTS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<EventComment[]>) => {
        return response;
      },
    }),

    // submit event comment
    submitEventComment: builder.mutation<
      VMResponse<CameraEventGetVideoDetailResponse>,
      { ticketId: string; content: string }
    >({
      query: ({ ticketId, content }) => {
        const formData = new FormData();
        formData.append("ticket", ticketId);
        formData.append("content", content);
        return {
          url: VM_ENDPOINTS.SUBMIT_EVENT_COMMENT,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [CamerasAdminVMApiTags.EVENT_COMMENTS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: VMResponse<CameraEventGetVideoDetailResponse>
      ) => {
        return response;
      },
    }),

    getTaskStatusById: builder.query<
      VMResponse<CameraTaskProcessResponse>,
      string
    >({
      query: (taskId: string) => {
        return {
          url: VM_ENDPOINTS.TASK_STATUS(taskId),
          method: API_METHODS.GET,
          params: { status: Math.random() }, // Add random unique param with each request to get latest data
        };
      },
      providesTags: [CamerasAdminVMApiTags.EVENT_COMMENTS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<CameraTaskProcessResponse>) => {
        return response;
      },
    }),

    getCameraConfig: builder.query<VMResponse<CameraDeviceConfig>, string>({
      query: (cameraId: string) => {
        return {
          url: VM_ENDPOINTS.CAMERA_CONFIG(cameraId),
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        CamerasAdminVMApiTags.SINGLE_CAMERA_CONFIG,
        CamerasAdminVMApiTags.EVENT_COMMENTS,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<CameraDeviceConfig>) => {
        return response;
      },
    }),

    dashNotify: builder.query<VMDashNotifyResponse, void>({
      query: () => {
        return {
          url: VM_ENDPOINTS.DASH_NOTIFY,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMDashNotifyResponse) => {
        return response;
      },
    }),

    incomingVoicecallAnswer: builder.mutation<any, string>({
      query: (request_id: string) => {
        return {
          url: VM_ENDPOINTS.INCOMING_VOICECALL_ANSWER(request_id),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    incomingVoicecallDecline: builder.mutation<any, string>({
      query: (request_id: string) => {
        return {
          url: VM_ENDPOINTS.INCOMING_VOICECALL_DECLINE(request_id),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetAllDeviceIdsQuery,
  useLazyGetAllDeviceIdsQuery,
  useGetAllAvailableDeviceModelsQuery,
  useGetLiveviewUrlQuery,
  useGetCameraDetailsQuery,
  useGetCameraByCidQuery,
  useLazyGetCameraByCidQuery,
  useGetDataUsageQuery,
  useLazyGetDataUsageQuery,
  useGetGroupListQuery,
  useGetDeviceHealthQuery,
  useGetDeviceHealthSimpleQuery,
  useFormatSDCardMutation,
  useGetDeviceICCHistoryQuery,
  useLazyGetDeviceWakeupQuery,
  useGetDeviceGeneralInfoQuery,
  useLazyGetDeviceGeneralInfoQuery,
  useUpdateConfigurationSingleDeviceMutation,
  useLazyGetDiagnoseDeviceQuery,
  useGetLastParkingPhotoQuery,
  useGetEventDetailsQuery,
  useEventGetVideoDetailsMutation,
  useGetEventVideoDownloadUrlMutation,
  useDeleteSDCardFileMutation,
  useGetDeviceStorageListingQuery,
  useLazyGetDeviceStorageListingQuery,
  useGetDeviceTimelineQuery,
  useGetEventCommentsQuery,
  useSubmitEventCommentMutation,
  useGetTaskStatusByIdQuery,
  useLazyGetTaskStatusByIdQuery,
  useGetCameraConfigQuery,
  useDashNotifyQuery,
  useIncomingVoicecallAnswerMutation,
  useIncomingVoicecallDeclineMutation,
} = VMAdminAPIs;
